import { z } from 'zod';
import { PLANS } from './modules.js';
/**
 * APIs und Webhooks (KON-186 bis KON-194): Verbindungen eines Mandanten zu Diensten außerhalb von Wirkia. Jede Verbindung hat Einstellungen, die der Mandant selbst
 * einträgt oder über eine Anmeldung beim Dienst (OAuth) erteilt, und Geheimnisse (Passwörter, Schlüssel, Tokens), die verschlüsselt liegen und nie zurückgegeben werden.
 */
export const INTEGRATION_KEYS = ['email', 'stripe', 'paypal', 'facebook', 'linkedin', 'tiktok'];
export const CAPABILITY_LABELS = {
    send_mail: 'E-Mails aus Wirkia versenden',
    payment_links: 'Zahlungslinks auf Rechnungen, Zahlungseingang automatisch verbuchen',
    publish_posts: 'Beiträge veröffentlichen',
    receive_leads: 'Anfragen aus Kontaktformularen empfangen',
    receive_messages: 'Nachrichten empfangen und beantworten',
};
export const INTEGRATIONS = [
    { key: 'email', label: 'E-Mail-Versand', description: 'Das Postfach Ihres Unternehmens, über das Wirkia Angebote, Newsletter und Rechnungen verschickt. Ohne diese Einrichtung versendet Wirkia keine E-Mails in Ihrem Namen.', plan: 'basic', capabilities: ['send_mail'] },
    { key: 'stripe', label: 'Stripe', description: 'Ihr Stripe-Konto: Kunden bezahlen eine Rechnung per Karte oder Lastschrift über einen Link, der Zahlungseingang wird auf der Rechnung verbucht.', plan: 'standard', capabilities: ['payment_links'] },
    { key: 'paypal', label: 'PayPal', description: 'Ihr PayPal-Geschäftskonto: Kunden bezahlen eine Rechnung über einen Link, der Zahlungseingang wird auf der Rechnung verbucht.', plan: 'standard', capabilities: ['payment_links'] },
    { key: 'facebook', label: 'Facebook', description: 'Ihre Facebook-Seite: Beiträge veröffentlichen, Anfragen aus Lead-Formularen und Nachrichten aus dem Messenger in Wirkia empfangen und beantworten.', plan: 'premium', capabilities: ['publish_posts', 'receive_leads', 'receive_messages'] },
    { key: 'linkedin', label: 'LinkedIn', description: 'Ihre LinkedIn-Unternehmensseite: Beiträge veröffentlichen. LinkedIn bietet für Nachrichten und Formulare keine freie Schnittstelle.', plan: 'premium', capabilities: ['publish_posts'] },
    { key: 'tiktok', label: 'TikTok', description: 'Ihr TikTok-Konto: Videos veröffentlichen. TikTok bietet für Nachrichten und Formulare keine freie Schnittstelle.', plan: 'premium', capabilities: ['publish_posts'] },
];
export const integrationDefinition = (key) => INTEGRATIONS.find((i) => i.key === key);
export const isIntegrationKey = (v) => INTEGRATION_KEYS.includes(v);
/** Darf ein Mandant mit diesem Paket die Verbindung nutzen? */
export function integrationAvailable(plan, key) {
    return PLANS.indexOf(plan) >= PLANS.indexOf(integrationDefinition(key).plan);
}
export const INTEGRATION_STATUS_LABELS = { not_connected: 'Nicht verbunden', connected: 'Verbunden', error: 'Fehler' };
// --- E-Mail -------------------------------------------------------------------------
export const EMAIL_PORTS = [465, 587, 2525, 25];
export const EMAIL_SECURITY = ['ssl', 'starttls', 'none'];
export const EMAIL_SECURITY_LABELS = { ssl: 'SSL/TLS (Port 465)', starttls: 'STARTTLS (Port 587)', none: 'Unverschlüsselt (nur Entwicklung)' };
const hostname = z
    .string()
    .trim()
    .toLowerCase()
    .min(3, 'Server fehlt')
    .max(253)
    .regex(/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/, 'Bitte nur den Servernamen angeben, zum Beispiel smtp.ionos.de')
    .refine((v) => !v.includes('..') && v.includes('.'), 'Bitte den vollständigen Servernamen angeben, zum Beispiel smtp.ionos.de');
export const emailSettingsInput = z
    .object({
    host: hostname,
    /** Im Betrieb prüft der Server zusätzlich, dass es einer der Ports aus EMAIL_PORTS ist (in Entwicklung und Tests ist jeder erlaubt) */
    port: z.number('Bitte einen Port angeben').int().min(1, 'Bitte einen Port angeben').max(65535, 'Ungültiger Port'),
    security: z.enum(EMAIL_SECURITY, 'Bitte die Verschlüsselung wählen'),
    user: z.string().trim().min(1, 'Benutzername fehlt').max(200),
    /** Fehlt das Passwort, bleibt das gespeicherte erhalten (nur beim Ändern einer vorhandenen Einrichtung) */
    password: z.string().min(1, 'Passwort fehlt').max(200).optional(),
    fromName: z.string().trim().max(80).optional().transform((v) => v || undefined),
    fromAddress: z.string().trim().toLowerCase().pipe(z.email('Bitte eine gültige Absenderadresse angeben')),
    replyTo: z.string().trim().toLowerCase().max(200).optional().transform((v) => v || undefined).pipe(z.email('Bitte eine gültige Antwortadresse angeben').optional()),
})
    .strict();
// --- Stripe und PayPal des Mandanten, Zahlungslinks auf Rechnungen (KON-189) -------------------------------------------------------------------------
export const PAYMENT_PROVIDERS = ['stripe', 'paypal'];
/** Stripe: geheimer oder eingeschränkter Schlüssel (sk_… oder rk_…) und das Signaturgeheimnis des Webhooks (whsec_…). Beim ersten Speichern genügt der Schlüssel, das Webhook-Geheimnis folgt. */
export const stripeSettingsInput = z
    .object({
    secretKey: z
        .string()
        .trim()
        .regex(/^(sk|rk)_(test|live)_[A-Za-z0-9]{10,}$/, 'Das ist kein Stripe-Schlüssel. Er beginnt mit sk_live_, sk_test_, rk_live_ oder rk_test_.')
        .optional(),
    webhookSecret: z.string().trim().regex(/^whsec_[A-Za-z0-9]{10,}$/, 'Das Signaturgeheimnis beginnt mit whsec_').optional(),
})
    .strict()
    .refine((v) => v.secretKey !== undefined || v.webhookSecret !== undefined, 'Bitte einen Schlüssel oder das Signaturgeheimnis angeben');
export const PAYPAL_ENVIRONMENTS = ['sandbox', 'live'];
export const paypalSettingsInput = z
    .object({
    environment: z.enum(PAYPAL_ENVIRONMENTS, 'Bitte Test (Sandbox) oder Live wählen').optional(),
    clientId: z.string().trim().min(10, 'Client-ID fehlt').max(200).optional(),
    secret: z.string().trim().min(10, 'Secret fehlt').max(300).optional(),
    webhookId: z.string().trim().min(5, 'Webhook-ID fehlt').max(100).optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Bitte Angaben machen');
/**
 * Wohin der Kunde nach dem Bezahlen kommt (Einrichtung → APIs und Webhooks, gilt für Stripe und PayPal). Ein Mandant mit eigener Webseite trägt hier seine Seiten ein, zum Beispiel
 * „https://www.beispiel.de/danke“. Ohne Angabe landet der Kunde auf der neutralen Seite von Wirkia. In der Adresse steht {rechnung} für die Rechnungsnummer, damit die Seite sie anzeigen kann.
 */
export const PAYMENT_RETURN_PLACEHOLDER = '{rechnung}';
const returnUrl = z
    .string()
    .trim()
    .max(500, 'Die Adresse ist zu lang.')
    .nullable()
    .transform((v) => (v === null || v === '' ? null : v))
    .refine((v) => {
    if (v === null)
        return true;
    const probe = v.split(PAYMENT_RETURN_PLACEHOLDER).join('RE-1');
    // https, ein Rechnername mit Punkt und keine Zugangsdaten in der Adresse
    return z.url().safeParse(probe).success && /^https:\/\/[^/?#@\s]+\.[^/?#@\s]+/i.test(probe);
}, 'Bitte geben Sie eine vollständige Adresse mit https:// an, zum Beispiel https://www.beispiel.de/danke.');
export const paymentReturnInput = z.object({ successUrl: returnUrl, cancelUrl: returnUrl }).strict();
export const paymentLinkInput = z.object({ provider: z.enum(PAYMENT_PROVIDERS, 'Bitte einen Zahlungsdienst wählen') }).strict();
export const sendPaymentLinkInput = z
    .object({ to: z.string().trim().toLowerCase().pipe(z.email('Bitte eine gültige E-Mail-Adresse angeben')).optional() })
    .strict();
export const PAYMENT_LINK_STATUS_LABELS = { open: 'Offen', paid: 'Bezahlt', cancelled: 'Zurückgezogen' };
// --- Soziale Netzwerke: Facebook, LinkedIn, TikTok (KON-190 bis KON-193) ---------------------------------------------------------------------------------
export const SOCIAL_PROVIDERS = ['facebook', 'linkedin', 'tiktok'];
export const SOCIAL_LABELS = { facebook: 'Facebook', linkedin: 'LinkedIn', tiktok: 'TikTok' };
export const isSocialProvider = (v) => SOCIAL_PROVIDERS.includes(v);
export const socialAccountInput = z.object({ accountId: z.string().trim().min(1, 'Bitte eine Seite wählen').max(100).regex(/^[A-Za-z0-9_-]+$/, 'Ungültige Kennung') }).strict();
/** Beiträge (KON-193): Text und Link für Facebook und LinkedIn, ein Video für TikTok */
export const SOCIAL_POST_LIMITS = { text: 3000, tiktokTitle: 2200 };
export const socialPostInput = z
    .object({
    text: z.string().trim().max(SOCIAL_POST_LIMITS.text, `Höchstens ${SOCIAL_POST_LIMITS.text} Zeichen`).optional().transform((v) => v || undefined),
    link: z.string().trim().max(500).url('Bitte eine vollständige Internetadresse angeben, zum Beispiel https://…').refine((u) => /^https:\/\//.test(u), 'Der Link muss mit https:// beginnen').optional().or(z.literal('').transform(() => undefined)),
    videoUrl: z.string().trim().max(500).url('Bitte eine vollständige Internetadresse angeben').refine((u) => /^https:\/\//.test(u), 'Die Adresse muss mit https:// beginnen').optional().or(z.literal('').transform(() => undefined)),
    providers: z.array(z.enum(SOCIAL_PROVIDERS)).min(1, 'Bitte mindestens ein Netzwerk wählen').max(3),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.providers.includes('facebook') && !v.text && !v.link)
        ctx.addIssue({ code: 'custom', path: ['text'], message: 'Für Facebook braucht der Beitrag einen Text oder einen Link' });
    if (v.providers.includes('linkedin') && !v.text)
        ctx.addIssue({ code: 'custom', path: ['text'], message: 'Für LinkedIn braucht der Beitrag einen Text' });
    if (v.providers.includes('tiktok') && !v.videoUrl)
        ctx.addIssue({ code: 'custom', path: ['videoUrl'], message: 'Für TikTok braucht der Beitrag die Adresse eines Videos' });
});
/** Rechnung per E-Mail an den Kunden: PDF im Anhang, wahlweise an eine andere Adresse und mit eigenem Text */
export const sendInvoiceInput = z
    .object({
    to: z.string().trim().toLowerCase().pipe(z.email('Bitte eine gültige E-Mail-Adresse angeben')).optional(),
    message: z.string().trim().max(2000, 'Höchstens 2000 Zeichen').optional(),
})
    .strict();
//# sourceMappingURL=integrations.js.map